import React, { useLayoutEffect, useRef, useCallback, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { angularInjectorProvider } from 'Injector';
import { reactRouterProvider } from 'FrontRoyalRouter/reactRouterProvider';
import { selectRouteTransition, routeTransitionActions } from './routeTransitionSlice';
import { type Phase } from './RouteAnimation.types';
import './RouteAnimation.scss';

type DispatchFn = ReturnType<typeof useDispatch>;
type ScrollHelper = {
    scrollToTop: () => void;
};

type Props = {
    children: ReactNode;
};

// A blocked navigation never changes the location (see useAllowBlockNavigation). Without this
// limit, the wait would never end.
export const NAVIGATE_COMPLETE_TIMEOUT_MS = 2000;

const slideInStartClasses = ['route-slide-in-from-left-start', 'route-slide-in-from-right-start'] as const;
const slideInClasses = ['route-slide-in-from-left', 'route-slide-in-from-right'] as const;
const slideOutClasses = ['route-slide-out-to-left', 'route-slide-out-to-right'] as const;
const allSlideClasses = [...slideInClasses, ...slideOutClasses, ...slideInStartClasses] as const;

type SlideClasses = {
    in: (typeof slideInClasses)[number];
    inStart: (typeof slideInStartClasses)[number];
    out: (typeof slideOutClasses)[number];
};

const SLIDE_CONFIGS: Record<'forward' | 'backward', SlideClasses> = {
    forward: {
        in: 'route-slide-in-from-right',
        inStart: 'route-slide-in-from-right-start',
        out: 'route-slide-out-to-left',
    },
    backward: {
        in: 'route-slide-in-from-left',
        inStart: 'route-slide-in-from-left-start',
        out: 'route-slide-out-to-right',
    },
};

function getSlideClasses(direction: 'forward' | 'backward'): SlideClasses {
    return SLIDE_CONFIGS[direction];
}

function cancelTimeoutRef(ref: React.RefObject<number | null>) {
    if (ref.current !== null) {
        window.clearTimeout(ref.current);
        ref.current = null;
    }
}

function placeContentOffScreen(content: HTMLDivElement, direction: 'forward' | 'backward') {
    const startClass = getSlideClasses(direction).inStart;
    content.classList.add(startClass);
}

function removeAllSlideClasses(content: HTMLDivElement) {
    allSlideClasses.forEach(className => {
        content.classList.remove(className);
    });
}

function prepareForIncoming(content: HTMLDivElement, direction: 'forward' | 'backward') {
    removeAllSlideClasses(content);
    placeContentOffScreen(content, direction);
}

function handlePhase({
    content,
    phase,
    direction,
    destination,
    transitionId,
    dispatch,
    locationKey,
    preNavigateLocationKeyRef,
    delayIncomingRef,
    navigateTimeoutRef,
}: {
    content: HTMLDivElement;
    phase: Phase;
    direction: 'forward' | 'backward';
    destination: string | number | null;
    transitionId: number | null;
    dispatch: DispatchFn;
    locationKey: string;
    preNavigateLocationKeyRef: React.RefObject<string | null>;
    delayIncomingRef: React.RefObject<number | null>;
    navigateTimeoutRef: React.RefObject<number | null>;
}) {
    if (phase === 'idle') {
        removeAllSlideClasses(content);
        return undefined;
    }

    if (!transitionId) {
        throw new Error('Transition ID is required with non-idle phase');
    }

    const slideClasses = getSlideClasses(direction);

    switch (phase) {
        // When animatePathChange is called, the phase is initialized at `outgoing`, and we
        // animate the old content off the screen.
        case 'outgoing':
            // End the phase if the out-class is already applied. There is nothing to animate.
            if (content.classList.contains(slideClasses.out)) {
                dispatch(routeTransitionActions.completeOutgoingPhase({ transitionId, now: Date.now() }));
                return undefined;
            }

            removeAllSlideClasses(content);
            content.classList.add(slideClasses.out);
            return undefined;

        // Once the old content is animated off the screen, we call navigate() to start rendering
        // the new content
        case 'triggerNavigate':
            preNavigateLocationKeyRef.current = locationKey;
            reactRouterProvider.navigate(destination as never);
            dispatch(routeTransitionActions.navigationTriggered({ transitionId, now: Date.now() }));
            return undefined;

        // After we have called navigate(), we wait until the new content has been rendered.
        // Once it is rendered, we place it off the screen, wait a beat, then animate it on
        //
        // `useNavigation().state` stays `idle` for the whole transition. Therefore the location key
        // is the only reliable signal.
        case 'waitForNavigateComplete': {
            if (locationKey === preNavigateLocationKeyRef.current) {
                navigateTimeoutRef.current = window.setTimeout(() => {
                    dispatch(routeTransitionActions.cancelTransition({ transitionId, now: Date.now() }));
                }, NAVIGATE_COMPLETE_TIMEOUT_MS);
                return () => {
                    cancelTimeoutRef(navigateTimeoutRef);
                };
            }

            prepareForIncoming(content, direction);
            delayIncomingRef.current = window.setTimeout(() => {
                dispatch(routeTransitionActions.beginIncoming({ transitionId, now: Date.now() }));
            });
            return () => {
                cancelTimeoutRef(delayIncomingRef);
            };
        }

        // During the `incoming` phase, the new page content is being animated onto the screen
        case 'incoming': {
            // End the phase if the start-class is absent. There is nothing to animate.
            if (!content.classList.contains(slideClasses.inStart)) {
                dispatch(routeTransitionActions.completIncomingPhase({ transitionId, now: Date.now() }));
                return undefined;
            }

            const scrollHelper = angularInjectorProvider.get<ScrollHelper>('scrollHelper');
            if (scrollHelper && typeof scrollHelper.scrollToTop === 'function') {
                scrollHelper.scrollToTop();
            }
            content.classList.add(slideClasses.in);
            return undefined;
        }
    }

    return undefined;
}

function useRouteAnimationPhase({ contentRef }: { contentRef: React.RefObject<HTMLDivElement | null> }) {
    const dispatch = useDispatch();
    const delayIncomingRef = useRef<number | null>(null);
    const navigateTimeoutRef = useRef<number | null>(null);
    const preNavigateLocationKeyRef = useRef<string | null>(null);
    const location = useLocation();
    const { phase, direction, transitionId, destination, startTimes } = useSelector(selectRouteTransition);
    const phaseStartedAt = startTimes[phase];

    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!content) return undefined;

        const phaseCleanupFn = handlePhase({
            content,
            phase,
            direction,
            destination,
            transitionId,
            dispatch,
            locationKey: location.key,
            preNavigateLocationKeyRef,
            delayIncomingRef,
            navigateTimeoutRef,
        });

        return phaseCleanupFn;
        // react-router returns a fresh location object per navigation, so `location` covers every
        // field this effect reads.
    }, [destination, direction, dispatch, location, phase, phaseStartedAt, transitionId, contentRef]);

    return { transitionId, phase, startTimes };
}

function getTransformDuration(el: Element) {
    const styles = getComputedStyle(el);

    const properties = styles.transitionProperty.split(',').map(p => p.trim());
    const durations = styles.transitionDuration.split(',').map(d => d.trim());
    const delays = styles.transitionDelay.split(',').map(d => d.trim());

    const index = properties.indexOf('transform');
    if (index === -1) return 0; // no transform transition defined

    const parseTime = (str: string) => (str.includes('ms') ? parseFloat(str) : parseFloat(str) * 1000);

    const duration = parseTime(durations[index] || '0s');
    const delay = parseTime(delays[index] || '0s');

    return duration + delay; // total time in ms
}

function useHandleTransitionEndCallback({
    contentRef,
    transitionId,
    phase,
    startTimes,
}: {
    contentRef: React.RefObject<HTMLDivElement | null>;
    transitionId: number | null;
    phase: Phase;
    startTimes: Record<Phase, number | null>;
}) {
    const dispatch = useDispatch();

    return useCallback(
        (event: React.TransitionEvent<HTMLDivElement>) => {
            const content = contentRef.current;
            if (event.currentTarget !== content) return;
            if (event.propertyName !== 'transform') return;
            if (!content || transitionId === null) return;

            const startTime = startTimes[phase];
            const elapsedTime = startTime ? Date.now() - startTime : null;
            const expectedTransitionDuration = getTransformDuration(content);

            // This feels like a hack, but we kept seeing a transitionend event trigger early on during the
            // animation. Despite a lot of digging, I could not figure out what was causing it or how to
            // avoid it. We need to ignore that one and wait for the one that actually comes at the end of the
            // transition
            if (elapsedTime && elapsedTime < expectedTransitionDuration - 100) {
                return;
            }

            if (phase === 'outgoing') {
                dispatch(routeTransitionActions.completeOutgoingPhase({ transitionId, now: Date.now() }));
            } else if (phase === 'incoming') {
                dispatch(routeTransitionActions.completIncomingPhase({ transitionId, now: Date.now() }));
            }
        },
        [dispatch, phase, transitionId, contentRef, startTimes],
    );
}

export const AnimatableRouteContent: React.FC<Props> = ({ children }) => {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { transitionId, phase, startTimes } = useRouteAnimationPhase({ contentRef });
    const handleTransitionEnd = useHandleTransitionEndCallback({
        contentRef,
        transitionId,
        phase,
        startTimes,
    });

    return (
        <div className="route-animation-viewport overflow-y-auto overflow-x-hidden">
            <div ref={contentRef} className="route-animation-layer" onTransitionEnd={handleTransitionEnd}>
                {children}
            </div>
        </div>
    );
};
